import React, { useState } from "react";
import { Col, Figure, Form, Row } from "react-bootstrap";
import { ValidationMessage } from "../../../../utils/constants/validationMessage";
import {
  floatInput,
  integerInput,
  whiteSpaceCheck,
} from "../../../../helper/helper";
import { MaxLength } from "../../../../utils/constants/CharactersLength";
import { ErrorMessage } from "@hookform/error-message";
import ODatePicker from "../../../../components/shared/datePicker/ODatePicker";
import ReactEasyCrop from "react-easy-crop";
import CropImageModal from "../../../../components/shared/modal/CropImageModal";
import useToastContext from "../../../../hooks/useToastContext";

const MainInformation = ({
  register,
  errors,
  setPhone,
  phone,
  setValue,
  dobDate,
  handleChangeDate,
  dateError,
  setProfilePic,
  profilePic,
  setProfileFile,
}) => {
  const notification = useToastContext();

  // ALL STATE
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const handleProfileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
      if (!allowedTypes.includes(file.type)) {
        notification.error(
          "Please upload an image in PNG, JPEG or JPG format."
        );
        return;
      }
      if (file.size > 300 * 1024) {
        notification.error("Please upload an image smaller than 200 KB.");
        return;
      }
      const blobURL = URL.createObjectURL(file);
      setImageToCrop(blobURL);
      setShowCropModal(true);
      setProfileFile(file);
    }
  };

  const saveCroppedImage = (croppedAreaPixels) => {
    if (!croppedAreaPixels || !imageToCrop) return;

    const image = new Image();
    image.src = imageToCrop;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      // Generate a dynamic filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      var dynamicFileName = `cropped-image-${timestamp}.png`;
      dynamicFileName.replace("-", "_");

      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], dynamicFileName, {
          type: "image/png",
        });
        setProfilePic(URL.createObjectURL(croppedFile)); // Update the profile picture
        setProfileFile(croppedFile); // Save the cropped file
      }, "image/png");
    };
  };

  return (
    <div className="wrapper mw-100 position-relative mb-5 pb-2 px-md-4 px-3">
      <span className="wrapper-heading">Main Information</span>
      <Row className="mb-3 gx-sm-3 gx-2">
        <Col md={12} className="mb-md-4 mb-2">
          <div className="profile-container">
            <Figure className="profile-figure">
              <Figure.Image
                src={profilePic}
                alt="Profile"
                className="profile-image"
              />
              <span className="camera-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="camera-svg"
                >
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
                  <circle cx="12" cy="13" r="3"></circle>
                </svg>
              </span>
              <input
                type="file"
                accept="image/*"
                className="profile-file-input"
                onChange={handleProfileUpload}
              />
            </Figure>
          </div>

          {/* Crop Image Modal */}
          {showCropModal && (
            <CropImageModal
              show={showCropModal}
              handleClose={() => setShowCropModal(false)}
              imageSrc={imageToCrop}
              saveCroppedImage={saveCroppedImage}
            />
          )}
        </Col>

        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formReferencePerson">
            <Form.Label>Bio Data Gender</Form.Label>
            <Form.Select
              name="gender"
              {...register("gender", {
                required: ValidationMessage?.GENDER_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            >
              <option value="">Select gender</option>
              <option value="female">Girl</option>
              <option value="male">Boy</option>
            </Form.Select>

            <ErrorMessage
              errors={errors}
              name="gender"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>

        <Col xl={3} md={4} xs={6} className="mb-3">
          <Form.Group controlId="formSurname">
            <Form.Label>
              First Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="First name"
              name="firstName"
              {...register("firstName", {
                required: ValidationMessage.FIRST_NAME_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
              maxLength={MaxLength.nameLen}
            />
            <ErrorMessage
              errors={errors}
              name="firstName"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} xs={6} className="mb-3">
          <Form.Group controlId="formGivenName">
            <Form.Label>
              Last Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Last name"
              name="lastName"
              {...register("lastName", {
                required: ValidationMessage?.LAST_NAME_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
              maxLength={MaxLength.nameLen}
            />
            <ErrorMessage
              errors={errors}
              name="lastName"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formYearOfBirth">
            <Form.Label>
              Date of birth <span className="text-danger">*</span>
            </Form.Label>

            <ODatePicker
              name="yearOfBirth"
              value={dobDate && new Date(dobDate)}
              placeholder={"dob"}
              errors={errors}
              onChange={handleChangeDate}
              dateError={dateError}
              className="form-control"
              maxDate={new Date()}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formYearOfBirth">
            <Form.Label>
              Time Of Birth <span className="text-danger">*</span>
            </Form.Label>

            <Form.Control
              type="text"
              placeholder="Enter your birth time"
              name="timeOfBirth"
              {...register("timeOfBirth", {
                required: ValidationMessage?.LAST_NAME_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
              maxLength={10}
            />
            <ErrorMessage
              errors={errors}
              name="timeOfBirth"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formReferencePerson">
            <Form.Label>Day</Form.Label>
            <Form.Select
              name="day"
              {...register("day", {
                required: ValidationMessage?.Day_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            >
              <option value="">Select gender</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thirsday">Thirsday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="sunday">Sunday</option>
            </Form.Select>

            <ErrorMessage
              errors={errors}
              name="day"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Place of birth</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your native place"
              name="placeOfBirth"
              {...register("placeOfBirth", {
                required: false,
                validate: (value) => whiteSpaceCheck(value),
              })}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Rasi</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your rashi"
              name="rashi"
              {...register("rashi", {
                required: ValidationMessage?.RASHI_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            />
            <ErrorMessage
              errors={errors}
              name="rashi"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Gotra</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your gotra"
              name="gotra"
              {...register("gotra", {
                required: ValidationMessage?.RASHI_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            />
            <ErrorMessage
              errors={errors}
              name="gotra"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Height</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your height"
              name="height"
              {...register("height", {
                required: ValidationMessage?.RASHI_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
              onKeyPress={(e) => floatInput(e)}
              maxLength={4}
            />
            <ErrorMessage
              errors={errors}
              name="height"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Weight</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your weight"
              name="weight"
              {...register("weight", {
                required: ValidationMessage?.RASHI_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
              onKeyPress={(e) => integerInput(e)}
              maxLength={3}
            />
            <ErrorMessage
              errors={errors}
              name="weight"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formNativePlace">
            <Form.Label>Personality</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your personality"
              name="personality"
              {...register("personality", {
                required: ValidationMessage?.PERSONALITY_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            />
            <ErrorMessage
              errors={errors}
              name="personality"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formReferencePerson">
            <Form.Label>Diet</Form.Label>
            <Form.Select
              name="diet"
              {...register("diet", {
                required: ValidationMessage?.Day_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            >
              <option value="">Select gender</option>
              <option value="Veg">Vegetarian</option>
              <option value="Non-Veg">Non-Vegetarian</option>
            </Form.Select>

            <ErrorMessage
              errors={errors}
              name="diet"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
        <Col xl={3} md={4} sm={6} className="mb-3">
          <Form.Group className="" controlId="formReferencePerson">
            <Form.Label>Marital Status</Form.Label>
            <Form.Select
              name="maritalStatus"
              {...register("maritalStatus", {
                required: ValidationMessage?.MARITAL_STATUS_REQUIRED,
                validate: (value) => whiteSpaceCheck(value),
              })}
            >
              <option value="">Select marital status</option>
              <option value="Single">Single</option>
              {/* <option value="Married">Married</option> */}
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </Form.Select>

            <ErrorMessage
              errors={errors}
              name="maritalStatus"
              render={({ message }) => (
                <p className="error-message text-start py-2 px-1 text-danger">
                  {message}
                </p>
              )}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default MainInformation;
